import type { ComponentWithSource } from "../api/componentsData"

export type ConnectorCollectionSource = {
  kind: "connector"
  connectorKey: string
  remoteCollection?: string
  live?: boolean
}

export type ComponentNavRecord = ComponentWithSource & {
  source?: ConnectorCollectionSource
  connectorLabel?: string
}

export type LocalCollectionFolder = {
  folderKey: string
  folderLabel: string
  components: ComponentNavRecord[]
}

export type ConnectorCollectionFolder = {
  folderKey: string
  connectorKey: string
  folderLabel: string
  components: ComponentNavRecord[]
}

export type CollectionMenuGrouping = {
  localFolders: LocalCollectionFolder[]
  connectorFolders: ConnectorCollectionFolder[]
}

function normalizeFolderName(raw: unknown): string {
  if (typeof raw !== "string") return ""
  return raw.trim().replace(/\s+/g, " ")
}

export function connectorFolderKey(connectorKey: string): string {
  return `__connector__:${connectorKey}`
}

export function isConnectorBackedCollection(component: ComponentNavRecord): boolean {
  const source = component.source
  return (
    source?.kind === "connector" &&
    typeof source.connectorKey === "string" &&
    source.connectorKey.trim().length > 0
  )
}

function sortByName(components: ComponentNavRecord[]): ComponentNavRecord[] {
  return [...components].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
}

function normalizeConnectorKey(key: string): string {
  return key.trim().toLowerCase()
}

export function groupCollectionsForMenu(components: ComponentNavRecord[]): CollectionMenuGrouping {
  const localComponents: ComponentNavRecord[] = []
  const connectorGroups = new Map<string, ComponentNavRecord[]>()

  for (const component of components) {
    if (isConnectorBackedCollection(component)) {
      const connectorKey = normalizeConnectorKey(component.source!.connectorKey)
      const existing = connectorGroups.get(connectorKey) ?? []
      existing.push(component)
      connectorGroups.set(connectorKey, existing)
      continue
    }
    localComponents.push(component)
  }

  const localGrouped = localComponents.reduce<Map<string, ComponentNavRecord[]>>((acc, component) => {
    const folder = normalizeFolderName(component.menuFolder)
    const key = folder || "__ungrouped__"
    const existing = acc.get(key) ?? []
    existing.push(component)
    acc.set(key, existing)
    return acc
  }, new Map())

  const localFolders = [...localGrouped.entries()]
    .filter(([, comps]) => comps.length > 0)
    .sort(([a], [b]) => {
      if (a === "__ungrouped__") return -1
      if (b === "__ungrouped__") return 1
      return a.localeCompare(b)
    })
    .map(([folderKey, comps]) => ({
      folderKey,
      folderLabel: folderKey === "__ungrouped__" ? "General" : folderKey,
      components: sortByName(comps),
    }))

  const connectorFolders = [...connectorGroups.entries()]
    .map(([connectorKey, comps]) => ({
      folderKey: connectorFolderKey(connectorKey),
      connectorKey,
      folderLabel:
        comps.find((c) => c.connectorLabel)?.connectorLabel ||
        connectorKey,
      components: sortByName(comps),
    }))
    .sort((a, b) => a.folderLabel.localeCompare(b.folderLabel))

  return { localFolders, connectorFolders }
}
